import { fromJS } from 'immutable';
import { createSelector } from 'reselect';

// Action Types
export const types = {
    CUSTOMER_LISTS_GET: 'CUSTOMER_LISTS_GET',
    CUSTOMER_LISTS_SUCCESS: 'CUSTOMER_LISTS_SUCCESS',
    CUSTOMER_LISTS_FAILURE: 'CUSTOMER_LISTS_FAILURE',
    CUSTOMER_LISTS_RESET: 'CUSTOMER_LISTS_RESET',

    CUSTOMER_LIST_CREATE: 'CUSTOMER_LIST_CREATE',
    CUSTOMER_LIST_CREATE_SUCCESS: 'CUSTOMER_LIST_CREATE_SUCCESS',
    CUSTOMER_LIST_CREATE_FAILURE: 'CUSTOMER_LIST_CREATE_FAILURE',

    CUSTOMER_LIST_UPDATE: 'CUSTOMER_LIST_UPDATE',
    CUSTOMER_LIST_UPDATE_SUCCESS: 'CUSTOMER_LIST_UPDATE_SUCCESS',
    CUSTOMER_LIST_UPDATE_FAILURE: 'CUSTOMER_LIST_UPDATE_FAILURE',

    CUSTOMER_LIST_DELETE: 'CUSTOMER_LIST_DELETE',
    CUSTOMER_LIST_DELETE_SUCCESS: 'CUSTOMER_LIST_DELETE_SUCCESS',
    CUSTOMER_LIST_DELETE_FAILURE: 'CUSTOMER_LIST_DELETE_FAILURE',

    CUSTOMER_LIST_DOWNLOAD: 'CUSTOMER_LIST_DOWNLOAD',
    CUSTOMER_LIST_DOWNLOAD_SUCCESS: 'CUSTOMER_LIST_DOWNLOAD_SUCCESS',
    CUSTOMER_LIST_DOWNLOAD_FAILURE: 'CUSTOMER_LIST_DOWNLOAD_FAILURE',
};

// Action Creators
export const actions = {
    // Fetch customer lists
    getCustomerLists: (premierAccessGroupId, params = {}) => ({
        type: types.CUSTOMER_LISTS_GET,
        payload: { premierAccessGroupId, params },
    }),

    getCustomerListsSuccess: (data) => ({
        type: types.CUSTOMER_LISTS_SUCCESS,
        payload: data,
    }),

    getCustomerListsFailure: (error) => ({
        type: types.CUSTOMER_LISTS_FAILURE,
        payload: error,
    }),

    resetCustomerLists: () => ({
        type: types.CUSTOMER_LISTS_RESET,
    }),

    // Create customer list
    createCustomerList: (listData) => ({
        type: types.CUSTOMER_LIST_CREATE,
        payload: listData,
    }),

    createCustomerListSuccess: (list) => ({
        type: types.CUSTOMER_LIST_CREATE_SUCCESS,
        payload: list,
    }),

    createCustomerListFailure: (error) => ({
        type: types.CUSTOMER_LIST_CREATE_FAILURE,
        payload: error,
    }),

    // Update customer list
    updateCustomerList: (listId, listData) => ({
        type: types.CUSTOMER_LIST_UPDATE,
        payload: { listId, listData },
    }),

    updateCustomerListSuccess: (list) => ({
        type: types.CUSTOMER_LIST_UPDATE_SUCCESS,
        payload: list,
    }),

    updateCustomerListFailure: (error) => ({
        type: types.CUSTOMER_LIST_UPDATE_FAILURE,
        payload: error,
    }),

    // Delete customer list
    deleteCustomerList: (listId) => ({
        type: types.CUSTOMER_LIST_DELETE,
        payload: { listId },
    }),

    deleteCustomerListSuccess: (listId) => ({
        type: types.CUSTOMER_LIST_DELETE_SUCCESS,
        payload: { listId },
    }),

    deleteCustomerListFailure: (error) => ({
        type: types.CUSTOMER_LIST_DELETE_FAILURE,
        payload: error,
    }),
};

// Initial State
const initialState = fromJS({
    lists: [],
    loading: false,
    error: null,
    total: 0,
    page: 1,
    perPage: 20,

    // CRUD operation states
    creating: false,
    createError: null,
    updating: false,
    updateError: null,
    deleting: false,
    deleteError: null,
});

// Reducer
const customerLists = (state = initialState, action) => {
    switch (action.type) {
    // Fetch customer lists
    case types.CUSTOMER_LISTS_GET:
        return state.merge({
            loading: true,
            error: null,
        });

    case types.CUSTOMER_LISTS_SUCCESS:
        return state.merge({
            loading: false,
            error: null,
            lists: fromJS(action.payload.data || action.payload.customer_lists || []),
            total: action.payload.total || 0,
            page: action.payload.page || 1,
            perPage: action.payload.perPage || 20,
        });

    case types.CUSTOMER_LISTS_FAILURE:
        return state.merge({
            loading: false,
            error: action.payload,
            lists: fromJS([]),
        });

    case types.CUSTOMER_LISTS_RESET:
        return initialState;

        // Create customer list
    case types.CUSTOMER_LIST_CREATE:
        return state.merge({
            creating: true,
            createError: null,
        });

    case types.CUSTOMER_LIST_CREATE_SUCCESS:
        return state.merge({
            creating: false,
            createError: null,
            lists: state.get('lists').push(fromJS(action.payload)),
            total: state.get('total') + 1,
        });

    case types.CUSTOMER_LIST_CREATE_FAILURE:
        return state.merge({
            creating: false,
            createError: action.payload,
        });

        // Update customer list
    case types.CUSTOMER_LIST_UPDATE:
        return state.merge({
            updating: true,
            updateError: null,
        });

    case types.CUSTOMER_LIST_UPDATE_SUCCESS: {
        const updatedList = fromJS(action.payload);
        const lists = state.get('lists');
        const index = lists.findIndex((list) => list.get('id') === updatedList.get('id'));

        return state.merge({
            updating: false,
            updateError: null,
            lists: index >= 0 ? lists.set(index, updatedList) : lists,
        });
    }

    case types.CUSTOMER_LIST_UPDATE_FAILURE:
        return state.merge({
            updating: false,
            updateError: action.payload,
        });

        // Delete customer list
    case types.CUSTOMER_LIST_DELETE:
        return state.merge({
            deleting: true,
            deleteError: null,
        });

    case types.CUSTOMER_LIST_DELETE_SUCCESS: {
        const listId = action.payload.listId;
        const lists = state.get('lists');
        const filteredLists = lists.filter((list) => list.get('id') !== listId);

        return state.merge({
            deleting: false,
            deleteError: null,
            lists: filteredLists,
            total: Math.max(0, state.get('total') - 1),
        });
    }

    case types.CUSTOMER_LIST_DELETE_FAILURE:
        return state.merge({
            deleting: false,
            deleteError: action.payload,
        });

    default:
        return state;
    }
};

// Selectors
const getCustomerListsData = (state) => state.getIn(['app', 'customerLists']);

export const selectors = {
    getCustomerListsData,
    getCustomerLists: createSelector(
        getCustomerListsData,
        (data) => data.get('lists', fromJS([])).toJS(),
    ),
    isCustomerListsLoading: createSelector(
        getCustomerListsData,
        (data) => data.get('loading', false),
    ),
    getCustomerListsError: createSelector(
        getCustomerListsData,
        (data) => data.get('error', null),
    ),
    getCustomerListsTotal: createSelector(
        getCustomerListsData,
        (data) => data.get('total', 0),
    ),
    getCustomerListsPage: createSelector(
        getCustomerListsData,
        (data) => data.get('page', 1),
    ),
    getCustomerListsPerPage: createSelector(
        getCustomerListsData,
        (data) => data.get('perPage', 20),
    ),

    // CRUD operation selectors
    isCreatingCustomerList: createSelector(
        getCustomerListsData,
        (data) => data.get('creating', false),
    ),
    getCreateCustomerListError: createSelector(
        getCustomerListsData,
        (data) => data.get('createError', null),
    ),
    isUpdatingCustomerList: createSelector(
        getCustomerListsData,
        (data) => data.get('updating', false),
    ),
    getUpdateCustomerListError: createSelector(
        getCustomerListsData,
        (data) => data.get('updateError', null),
    ),
    isDeletingCustomerList: createSelector(
        getCustomerListsData,
        (data) => data.get('deleting', false),
    ),
    getDeleteCustomerListError: createSelector(
        getCustomerListsData,
        (data) => data.get('deleteError', null),
    ),
};

export default customerLists;
